import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { patientAPI } from '../../api/patients';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const STAGES = [
  { id: 'new_lead', title: 'New Lead', color: '#607d8b' },
  { id: 'diagnostics', title: 'Diagnostics', color: '#0288d1' },
  { id: 'treatment_plan', title: 'Treatment Plan', color: '#7b1fa2' },
  { id: 'decision_pending', title: 'Decision Pending', color: '#ef6c00' },
  { id: 'treatment_started', title: 'Treatment Started', color: '#2e7d32' },
];

const PatientPipeline = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [board, setBoard] = useState({});
  const [analytics, setAnalytics] = useState({
    outcomes: { open: 0, won: 0, lost: 0 },
    stages: {},
    lostReasons: {},
    noShowRecovery: { open: 0, in_progress: 0, done: 0, cancelled: 0 },
    conversionRate: 0,
  });
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [outcomePatient, setOutcomePatient] = useState(null);
  const [lostReason, setLostReason] = useState('price');
  const [lostReasonNote, setLostReasonNote] = useState('');
  const [savingLostOutcome, setSavingLostOutcome] = useState(false);

  const loadPipeline = useCallback(async (searchValue = '') => {
    try {
      setLoading(true);
      const response = await patientAPI.getPipeline(searchValue.trim() ? { search: searchValue.trim() } : {});
      setBoard(response.data?.data || {});
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, formatFailedActionMessage('load', 'pipeline')));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const response = await patientAPI.getPipelineAnalytics();
      setAnalytics(response?.data || {});
    } catch (err) {
      // Non-blocking for board usage
    }
  }, []);

  useEffect(() => {
    loadPipeline();
    loadAnalytics();
  }, [loadPipeline, loadAnalytics]);

  const handleMove = async (patientId, stage) => {
    try {
      await patientAPI.updatePipelineStage(patientId, stage);
      toast.success('Stage updated');
      await loadPipeline(search);
      await loadAnalytics();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('update', 'stage')));
    }
  };

  const handleOutcome = async (patient, outcome) => {
    try {
      if (outcome === 'lost') {
        setOutcomePatient(patient);
        setLostReason(patient?.lostReason || 'price');
        setLostReasonNote(patient?.lostReasonNote || '');
        setLostDialogOpen(true);
        return;
      }

      await patientAPI.updatePipelineOutcome(patient._id, {
        outcome,
        moveToTreatmentStarted: outcome === 'won',
      });
      toast.success(`Outcome set to ${outcome}`);
      await loadPipeline(search);
      await loadAnalytics();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('update', 'outcome')));
    }
  };

  const handleSubmitLostOutcome = async () => {
    if (!outcomePatient?._id) return;
    try {
      setSavingLostOutcome(true);
      await patientAPI.updatePipelineOutcome(outcomePatient._id, {
        outcome: 'lost',
        lostReason,
        lostReasonNote,
      });
      setLostDialogOpen(false);
      setOutcomePatient(null);
      toast.success('Outcome set to lost');
      await loadPipeline(search);
      await loadAnalytics();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('update', 'outcome')));
    } finally {
      setSavingLostOutcome(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">Follow-up Pipeline</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search patient"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadPipeline(search);
            }}
          />
          <Button variant="outlined" onClick={() => loadPipeline(search)}>Search</Button>
          <Button variant="contained" onClick={() => navigate('/patients')}>Back to Patients</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Open</Typography>
              <Typography variant="h5">{analytics?.outcomes?.open || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Won</Typography>
              <Typography variant="h5" color="success.main">{analytics?.outcomes?.won || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Lost</Typography>
              <Typography variant="h5" color="error.main">{analytics?.outcomes?.lost || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Win Rate</Typography>
              <Typography variant="h5">{analytics?.conversionRate || 0}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Lost reasons</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(analytics?.lostReasons || {}).map(([reason, count]) => (
              <Chip key={reason} size="small" label={`${reason}: ${count}`} />
            ))}
          </Box>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>No-show recovery tasks</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Open: ${analytics?.noShowRecovery?.open || 0}`} />
            <Chip size="small" label={`In progress: ${analytics?.noShowRecovery?.in_progress || 0}`} color="primary" />
            <Chip size="small" label={`Done: ${analytics?.noShowRecovery?.done || 0}`} color="success" />
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ minHeight: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {STAGES.map((stage) => {
            const items = board[stage.id] || [];
            return (
              <Grid item xs={12} md={6} lg={3} key={stage.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>{stage.title}</Typography>
                      <Chip size="small" label={items.length} sx={{ bgcolor: stage.color, color: '#fff' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {items.map((patient) => (
                        <Card key={patient._id} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{patient.name || 'Unnamed patient'}</Typography>
                          <Typography variant="caption" color="text.secondary">ID: {patient.patientId || '—'}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{patient.phone || patient.email || '—'}</Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              color={patient.pipelineOutcome === 'won' ? 'success' : patient.pipelineOutcome === 'lost' ? 'error' : 'default'}
                              label={patient.pipelineOutcome || 'open'}
                            />
                            {patient.lostReason && <Chip size="small" label={patient.lostReason} />}
                          </Box>
                          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <InputLabel>Move to</InputLabel>
                            <Select
                              value={patient.followUpStage || 'new_lead'}
                              label="Move to"
                              onChange={(e) => handleMove(patient._id, e.target.value)}
                            >
                              {STAGES.map((s) => (
                                <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button size="small" color="success" onClick={() => handleOutcome(patient, 'won')}>Won</Button>
                            <Button size="small" color="error" onClick={() => handleOutcome(patient, 'lost')}>Lost</Button>
                            <Button size="small" onClick={() => handleOutcome(patient, 'open')}>Reopen</Button>
                          </Box>
                          <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/patients/${patient._id}`)}>
                            Open
                          </Button>
                        </Card>
                      ))}
                      {items.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No patients in this stage</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={lostDialogOpen} onClose={() => setLostDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Set Lost Reason</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Patient: {outcomePatient?.name || 'Unknown'}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select
              value={lostReason}
              label="Reason"
              onChange={(e) => setLostReason(e.target.value)}
            >
              <MenuItem value="price">Price</MenuItem>
              <MenuItem value="no_time">No time</MenuItem>
              <MenuItem value="fear">Fear</MenuItem>
              <MenuItem value="competitor">Competitor</MenuItem>
              <MenuItem value="no_response">No response</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            multiline
            minRows={2}
            value={lostReasonNote}
            onChange={(e) => setLostReasonNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLostDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitLostOutcome}
            disabled={savingLostOutcome}
          >
            {savingLostOutcome ? 'Saving...' : 'Save Lost Outcome'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientPipeline;
